import React, { useCallback, useContext, useMemo, useState } from "react";

/** @typedef {"Article" | "Function" | "Category"} HelpItemType */

/**
 * @typedef {object} HelpItem
 * @property {string} [Id]
 * @property {HelpItemType} [Type]
 */

/**
 * @typedef {object} HelpContextValue
 * @property {(item: HelpItem) => void} push
 * @property {() => void} pop
 * @property {HelpItem | null} top
 * @property {() => void} clear
 */

/** @typedef {import("react").Context<HelpContextValue | null>} HelpContext */

const HelpContext = React.createContext(/** @type {HelpContextValue | null} */(null));

/** @typedef {{ children: import("react").ReactNode }} HelpProviderProps */

/** @param {HelpProviderProps} props */
function HelpProvider({ children }) {
    const [stack, setStack] = useState(/** @type {HelpItem[]} */([]));

    /** @type {(item: HelpItem) => void} */
    const push = useCallback((item) => {
        setStack(prevStack => {
            const last = prevStack.at(-1);
            if (last && last.Id === item.Id && last.Type === item.Type) {
                return [...prevStack.slice(0, -1), { ...item }];
            }

            return [...prevStack, { ...item }];
        });
    }, []);

    /** @type {() => void} */
    const pop = useCallback(() => {
        setStack(prevStack => prevStack.slice(0, -1));
    }, []);

    /** @type {() => void} */
    const clear = useCallback(() => {
        setStack([]);
    }, []);

    const top = stack.at(-1) ?? null;

    const value = useMemo(() => ({
        push,
        pop,
        top,
        clear
    }), [push, pop, top, clear]);

    return (
        <HelpContext.Provider value={value}>
            {children}
        </HelpContext.Provider>
    );
}

function useHelpContext() {
    const context = useContext(HelpContext);

    if (!context) {
        throw new Error("useHelpContext must be used inside HelpProvider.");
    }

    return context;
}

export { HelpProvider, useHelpContext };